import * as Sentry from '@sentry/react';
import {
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  deleteUser as cognitoDeleteUser,
  fetchAuthSession,
  SignInOutput,
} from 'aws-amplify/auth';
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  signInWithRedirect,
} from 'firebase/auth';
import { fetchDeleteAccount, fetchGetUserInfo, fetchSaveUserInfo } from '~/api';
import { DeleteUserInfoRequest, OAuthProviders, UserState } from '~/types';
import { airbridgeEvent } from '~/utils/airbridge';
import { auth, providers } from '~/services/firebase';
import { useUserStore, useAuthStore } from '~/store';

export class AuthenticationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthenticationError';
  }
}

/**
 * Firebase & Cognito 통합 인증 서비스
 */
class AuthService {
  /**
   * Firebase OAuth 인증
   * 개발 환경인 경우 팝업으로 로그인
   * @param provider - 인증 제공자
   */
  static async signInWithOAuthPopup(
    provider: OAuthProviders,
  ): Promise<boolean> {
    try {
      const authProvider = providers[provider];
      const { user } = await signInWithPopup(auth, authProvider);
      if (!user) return false;
      const idToken = await user?.getIdToken();
      useAuthStore.getState().setIdToken(idToken);
      useAuthStore.getState().setAuthProvider('firebase');
      useAuthStore.getState().setLoginMethod(provider);
      return true;
    } catch (error) {
      console.error('Sign-in with OAuth popup error:', error);
      return false;
    }
  }

  /**
   * Firebase OAuth 인증
   * 운영 환경인 경우 리다이렉트로 로그인
   * @param provider - 인증 제공자
   */
  static async signInWithRedirect(provider: OAuthProviders): Promise<void> {
    const authProvider = providers[provider];
    await signInWithRedirect(auth, authProvider);
  }

  /**
   * Cognito 이메일 인증
   * @param email - 사용자 이메일
   * @param password - 사용자 비밀번호
   */
  static async signInWithEmail(
    email: string,
    password: string,
  ): Promise<SignInOutput> {
    const result = await cognitoSignIn({
      username: email,
      password,
    });

    const { tokens } = await fetchAuthSession();
    useAuthStore.getState().setIdToken(tokens?.idToken?.toString() ?? null);
    useAuthStore.getState().setAuthProvider('cognito');
    useAuthStore.getState().setLoginMethod('email');
    return result;
  }

  /**
   * 로그아웃
   */
  static async signOut(): Promise<boolean> {
    const { authProvider, setAuthLoading, resetAuth } = useAuthStore.getState();
    const { email, resetUserData } = useUserStore.getState();
    setAuthLoading(true);

    try {
      if (authProvider === 'firebase') {
        await firebaseSignOut(auth);
      } else if (authProvider === 'cognito') {
        await cognitoSignOut();
      }

      resetAuth();
      resetUserData();
      airbridgeEvent.signOut(email);
      return true;
    } catch (error) {
      console.error('Sign-out error:', error);
    } finally {
      setAuthLoading(false);
    }
    return false;
  }

  static async deleteUser(): Promise<boolean> {
    const { authProvider, setAuthLoading, resetAuth } = useAuthStore.getState();
    const { resetUserData } = useUserStore.getState();
    setAuthLoading(true);
    try {
      await fetchDeleteAccount({
        leave_memo: '',
      } as DeleteUserInfoRequest);

      if (authProvider === 'firebase') {
        await firebaseSignOut(auth);
      } else if (authProvider === 'cognito') {
        await cognitoDeleteUser();
      }

      resetUserData();
      resetAuth();
      return true;
    } catch (error) {
      console.log(error);
      Sentry.captureException(error);
    } finally {
      setAuthLoading(false);
    }
    return false;
  }

  /**
   * 토큰 갱신
   * @returns 갱신된 토큰 또는 null
   */
  static async refreshIdToken(): Promise<string | null> {
    const { authProvider, setIdToken } = useAuthStore.getState();

    try {
      if (authProvider === 'firebase') {
        const currentUser = auth.currentUser;
        if (!currentUser) return null;

        const newToken = await currentUser.getIdToken(true); // true: 강제 갱신
        setIdToken(newToken);
        return newToken;
      } else if (authProvider === 'cognito') {
        const { tokens } = await fetchAuthSession({ forceRefresh: true });
        const idToken = tokens?.idToken;

        if (idToken) {
          const tokenString = idToken.toString();
          setIdToken(tokenString);
          return tokenString;
        }
      }

      return null;
    } catch (error) {
      console.error('Token refresh error:', error);
    }
    return null;
  }

  /**
   * 사용자 정보 가져오기
   * 사용자 정보를 가져오는 API 호출
   * @param email - 사용자 이메일
   * @returns 사용자 정보 또는 null
   */
  static async getUserInfo() {
    try {
      const fcmToken = sessionStorage.getItem('appToken');

      const response = await fetchGetUserInfo({
        fcm_token: fcmToken,
      });

      if (response.status !== 200 || !response.data || !response.data.email) {
        return null;
      }

      return response.data;
    } catch (error) {
      console.error('Get user info error:', error);
    }
    return null;
  }

  /**
   * 인증 상태 확인
   * 인증 상태가 유효한 경우 true, 유효하지 않은 경우 false 반환
   */
  static async checkAuthStatus(): Promise<boolean> {
    const { authProvider, isAuthenticated, idToken } = useAuthStore.getState();

    if (!isAuthenticated || !idToken) return false;

    try {
      if (authProvider === 'firebase') {
        const currentUser = auth.currentUser;
        return !!currentUser;
      } else if (authProvider === 'cognito') {
        const { tokens } = await fetchAuthSession();
        return !!tokens?.idToken;
      }

      return false;
    } catch (error) {
      console.error('Auth status check error:', error);
      return false;
    }
  }

  /**
   * DB에 저장된 유저 정보 검증 및 로그인 정보 설정
   * @returns 로그인 정보 검증 결과
   */
  static async verifySignIn(): Promise<boolean> {
    const userInfo = await this.getUserInfo();

    if (!userInfo) {
      return false;
    }

    useUserStore.getState().setUser(userInfo);
    useAuthStore.getState().setAuthenticated(userInfo.email);

    return true;
  }

  static async getSessionEmail(): Promise<string | null> {
    const { authProvider } = useAuthStore.getState();
    if (authProvider === 'firebase') {
      const currentUser = auth.currentUser;
      return currentUser?.email ?? null;
    } else if (authProvider === 'cognito') {
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      return idToken?.payload.email?.toString() ?? null;
    }
    return null;
  }

  /**
   * DB에 사용자 정보 저장
   * @param userInfo - 사용자 정보
   * @returns 사용자 정보 저장 성공 여부
   */
  static async saveUserInfo(userInfo: UserState): Promise<boolean> {
    const { loginMethod } = useAuthStore.getState();
    try {
      const { status } = await fetchSaveUserInfo(userInfo);
      if (status === 200) {
        airbridgeEvent.signUp(userInfo.email, userInfo, loginMethod ?? 'email');
        useUserStore.getState().setUser({
          ...userInfo,
          birth_date:
            userInfo.birth_date.length === 8
              ? `${userInfo.birth_date.slice(0, 2)}-${userInfo.birth_date.slice(
                  2,
                  4,
                )}-${userInfo.birth_date.slice(4)}`
              : userInfo.birth_date,
        });
        useAuthStore.getState().setAuthenticated(userInfo.email);
        return true;
      }
    } catch (error) {
      Sentry.captureException(
        'error: check your fetchSaveUserInfo api request data.',
      );
    }
    return false;
  }

  /**
   * JWT 토큰을 디코딩하는 유틸리티 함수
   */
  private static decodeToken(token: string): { exp: number } | null {
    try {
      // Base64 디코딩 (2번째 부분이 페이로드)
      const payload = token.split('.')[1];
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Token decode error:', error);
      return null;
    }
  }

  /**
   * 토큰 만료 여부 확인
   */
  static isTokenExpired(token: string): boolean {
    if (!token) return true;

    const decodedToken = this.decodeToken(token);
    if (!decodedToken) return true;

    // 현재 시간과 만료 시간 비교
    const currentTime = Math.floor(Date.now() / 1000); // 초 단위 변환
    return decodedToken.exp < currentTime;
  }

  /**
   * 토큰이 곧 만료되는지 확인 (예: 5분 이내)
   */
  static isTokenExpiringSoon(token: string, timeBuffer = 5 * 60): boolean {
    if (!token) return true;

    const decodedToken = this.decodeToken(token);
    if (!decodedToken) return true;

    const currentTime = Math.floor(Date.now() / 1000);
    return decodedToken.exp < currentTime + timeBuffer;
  }
}

export default AuthService;
